// src/pages/TenantProperty.js
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getApiErrorMessage } from "../config/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Modal from "../components/ui/Modal";
import { marketService } from "../services/marketService";
import { planService } from "../services/planService";
import { profilerService } from "../services/profilerService";
import { tenantService } from "../services/tenantService";
import {
  getDuplicateRequestResolution,
  hasActiveSuccessfulPlanPayment,
  hasApprovedMarketplaceAccess,
  resolveMarketplaceAccessState,
} from "../utils/marketplaceAccess";
import {
  clearPendingPlanSelection,
  clearPendingSellerRequest,
  getPendingSellerRequest,
  persistPendingPlanSelection,
  persistPendingSellerRequest,
} from "../utils/sellerOnboarding";
import {
  Building2,
  Download,
  ArrowRight,
  RefreshCw,
  Store,
  LogOut,
  Sun,
  Moon,
  Mail,
  Phone,
  Check,
  X,
  Zap,
  Crown,
  Sparkles,
  ChevronLeft,
  ShoppingBag,
  BadgeCheck,
  FileText,
  CreditCard,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────

function daysRemaining(endDate: string | Date): number {
  const end = new Date(endDate).getTime();
  if (Number.isNaN(end)) {
    return Number.NaN;
  }

  const now = new Date().getTime();
  return Math.floor((end - now) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) {
    return "-";
  }

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleDateString("en-NG", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount) {
  const numericAmount = Number(amount ?? 0);
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(Number.isFinite(numericAmount) ? numericAmount : 0);
}

const normalizeMarketCategories = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.categories)) {
    return responseData.categories;
  }

  return [];
};

const normalizeRequestStatus = (status) =>
  String(status || "").trim().toLowerCase();

const firstDefinedValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
};

const firstArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
};

const toNumber = (value) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getDateTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  const parsedValue = new Date(value).getTime();
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeTenancyPayment = (payment, index) => {
  const paidAt = firstDefinedValue(
    payment?.paidAt,
    payment?.paymentDate,
    payment?.date,
    payment?.createdAt,
    payment?.updatedAt,
  );

  return {
    ...payment,
    id: String(
      firstDefinedValue(
        payment?.id,
        payment?._id,
        payment?.paymentId,
        payment?.reference,
        `${index}-${paidAt || "payment"}`,
      ),
    ),
    amount: toNumber(
      firstDefinedValue(
        payment?.amount,
        payment?.paidAmount,
        payment?.totalAmount,
        payment?.paymentAmount,
      ),
    ),
    status: firstDefinedValue(
      payment?.status,
      payment?.paymentStatus,
      payment?.state,
      "Recorded",
    ),
    paidAt,
    transactionReference: firstDefinedValue(
      payment?.transactionReference,
      payment?.reference,
      payment?.gatewayReference,
    ),
  };
};

const normalizeTenancy = (tenancy, index) => {
  const propertySource =
    tenancy?.property || tenancy?.unit?.property || tenancy?.listing?.property || {};
  const payments = firstArray(
    tenancy?.payments,
    tenancy?.paymentHistory,
    tenancy?.rentPayments,
    tenancy?.paymentSummary?.payments,
  )
    .map((payment, paymentIndex) => normalizeTenancyPayment(payment, paymentIndex))
    .sort((left, right) => getDateTimestamp(right.paidAt) - getDateTimestamp(left.paidAt));
  const totalDue = toNumber(
    firstDefinedValue(
      tenancy?.paymentSummary?.totalDue,
      tenancy?.totalDue,
      tenancy?.amountDue,
      tenancy?.rentAmount,
    ),
  );
  const totalPaidAmount = toNumber(
    firstDefinedValue(
      tenancy?.paymentSummary?.totalPaidAmount,
      tenancy?.paymentSummary?.totalPaid,
      tenancy?.totalPaidAmount,
      tenancy?.totalPaid,
    ),
  );
  const outstandingAmount = toNumber(
    firstDefinedValue(
      tenancy?.paymentSummary?.outstandingAmount,
      tenancy?.paymentSummary?.balance,
      tenancy?.outstandingAmount,
      totalDue - totalPaidAmount,
    ),
  );

  return {
    ...tenancy,
    id: String(
      firstDefinedValue(
        tenancy?.id,
        tenancy?._id,
        tenancy?.tenancyId,
        propertySource?.id,
        `tenancy-${index + 1}`,
      ),
    ),
    property: {
      ...propertySource,
      id: String(firstDefinedValue(propertySource?.id, tenancy?.propertyId, `property-${index + 1}`)),
      name: firstDefinedValue(
        propertySource?.name,
        propertySource?.title,
        tenancy?.propertyName,
        "Property",
      ),
      locationAddress: firstDefinedValue(
        propertySource?.locationAddress,
        propertySource?.address,
        propertySource?.street,
        tenancy?.locationAddress,
        "Address unavailable",
      ),
      locationState: firstDefinedValue(
        propertySource?.locationState,
        propertySource?.state,
        tenancy?.locationState,
        "",
      ),
    },
    rentAmount: toNumber(
      firstDefinedValue(
        tenancy?.rentAmount,
        tenancy?.amount,
        tenancy?.paymentSummary?.rentAmount,
      ),
    ),
    paymentPlan: firstDefinedValue(
      tenancy?.paymentPlan,
      tenancy?.paymentSummary?.planName,
      tenancy?.rentSchedule,
      "Lease",
    ),
    rentStartDate: firstDefinedValue(
      tenancy?.rentStartDate,
      tenancy?.startDate,
      tenancy?.leaseStartDate,
    ),
    rentEndDate: firstDefinedValue(
      tenancy?.rentEndDate,
      tenancy?.endDate,
      tenancy?.leaseEndDate,
      tenancy?.expiresAt,
    ),
    lastPaymentAt: firstDefinedValue(tenancy?.lastPaymentAt, payments[0]?.paidAt),
    latestPayment: payments[0] || null,
    payments,
    agreementId: firstDefinedValue(
      tenancy?.leasingAgreementId,
      tenancy?.agreementId,
      tenancy?.leasingAgreement?.id,
    ),
    paymentSummary: {
      totalDue,
      totalPaidAmount,
      outstandingAmount,
      paymentRequired: Boolean(
        firstDefinedValue(
          tenancy?.paymentSummary?.paymentRequired,
          tenancy?.paymentRequired,
          tenancy?.isPaymentDue,
          outstandingAmount > 0,
        ),
      ),
    },
  };
};

const normalizeTenantProfile = (responseData, profile) => {
  const tenantProfile = responseData?.tenant || responseData?.data?.tenant || profile?.tenant || {};
  const tenancies = firstArray(
    responseData?.tenancies,
    responseData?.data?.tenancies,
    responseData?.items,
    Array.isArray(responseData) ? responseData : null,
  ).map((tenancy, index) => normalizeTenancy(tenancy, index));

  return {
    ...responseData,
    firstName: firstDefinedValue(
      responseData?.firstName,
      tenantProfile?.firstName,
      profile?.firstName,
      profile?.name?.split(" ")?.[0],
    ),
    lastName: firstDefinedValue(
      responseData?.lastName,
      tenantProfile?.lastName,
      profile?.lastName,
      profile?.name?.split(" ")?.slice(1).join(" "),
    ),
    tenancies,
  };
};

const normalizeLeasingAgreement = (agreement, index) => ({
  ...agreement,
  id: String(
    firstDefinedValue(
      agreement?.id,
      agreement?._id,
      agreement?.agreementId,
      `agreement-${index + 1}`,
    ),
  ),
  name: firstDefinedValue(
    agreement?.title,
    agreement?.name,
    agreement?.agreementNumber,
    `Leasing Agreement ${index + 1}`,
  ),
  status: firstDefinedValue(
    agreement?.status,
    agreement?.agreementStatus,
    "Available",
  ),
  documentUrl: firstDefinedValue(
    agreement?.documentUrl,
    agreement?.document?.url,
    agreement?.fileUrl,
  ),
  tenancyId: String(
    firstDefinedValue(
      agreement?.tenancyId,
      agreement?.tenancy?.id,
      agreement?.lease?.tenancyId,
      "",
    ),
  ),
  propertyId: String(
    firstDefinedValue(
      agreement?.propertyId,
      agreement?.property?.id,
      agreement?.tenancy?.property?.id,
      "",
    ),
  ),
  propertyName: firstDefinedValue(
    agreement?.property?.name,
    agreement?.propertyName,
    agreement?.tenancy?.property?.name,
    "Assigned property",
  ),
  startDate: firstDefinedValue(
    agreement?.startDate,
    agreement?.leaseStartDate,
    agreement?.createdAt,
  ),
  endDate: firstDefinedValue(
    agreement?.endDate,
    agreement?.leaseEndDate,
    agreement?.expiresAt,
  ),
  updatedAt: firstDefinedValue(agreement?.updatedAt, agreement?.createdAt),
});

const normalizeLeasingAgreements = (responseData) =>
  firstArray(
    responseData?.agreements,
    responseData?.items,
    responseData?.data,
    Array.isArray(responseData) ? responseData : null,
  )
    .map((agreement, index) => normalizeLeasingAgreement(agreement, index))
    .sort(
      (left, right) =>
        getDateTimestamp(right.updatedAt || right.startDate) -
        getDateTimestamp(left.updatedAt || left.startDate),
    );

const findAgreementForTenancy = (tenancy, agreements) =>
  agreements.find(
    (agreement) =>
      agreement.id === tenancy?.agreementId ||
      (agreement.tenancyId && agreement.tenancyId === tenancy?.id) ||
      (agreement.propertyId && agreement.propertyId === tenancy?.property?.id),
  ) || null;

// ── Plan style maps ──────────────────────────────────────────────────────────

const PLAN_ACCENT = {
  gray: {
    header: "from-gray-800/60 to-gray-900/60",
    btn: "bg-gray-700 hover:bg-gray-600 text-gray-200",
    selectedRing: "ring-2 ring-gray-500",
    check: "text-gray-400",
  },
  violet: {
    header: "from-violet-900/50 to-indigo-900/40",
    btn: "bg-violet-600 hover:bg-violet-500 text-white",
    selectedRing: "ring-2 ring-violet-500",
    check: "text-violet-400",
  },
  amber: {
    header: "from-amber-900/30 to-orange-900/20",
    btn: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white",
    selectedRing: "ring-2 ring-amber-500",
    check: "text-amber-400",
  },
};

// ── Main component ───────────────────────────────────────────────────────────

export default function TenantProperty() {
  const { user, logout, refreshProfile } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const pendingSellerRequest = getPendingSellerRequest();
  const hasMarketplaceAccount = hasApprovedMarketplaceAccess(user);
  const requestStatus = normalizeRequestStatus(user?.requestStatus);
  const hasSubmittedSellerRequest =
    requestStatus !== "" && requestStatus !== "rejected";
  const hasPaidPlanWithoutRequest =
    !hasMarketplaceAccount &&
    user?.requestIsPaid === true &&
    Boolean(user?.planId) &&
    !hasSubmittedSellerRequest;

  const currentProfilerQuery = useQuery({
    queryKey: ["tenant-dashboard-profiler", user?.token],
    queryFn: async () => profilerService.getMe(user.token),
    enabled: Boolean(user?.token),
  });

  const currentProfiler = currentProfilerQuery.data || null;
  const currentProfilerId = currentProfiler?.id || user?.id || "";
  const tenantPhoneNumber = useMemo(
    () =>
      firstDefinedValue(
        currentProfiler?.phoneNumber,
        currentProfiler?.tenant?.phoneNumber,
        user?.phoneNumber,
        user?.phone,
        "",
      ),
    [
      currentProfiler?.phoneNumber,
      currentProfiler?.tenant?.phoneNumber,
      user?.phone,
      user?.phoneNumber,
    ],
  );
  const tenantProfileQuery = useQuery({
    queryKey: ["tenant-profile", tenantPhoneNumber],
    queryFn: async () => tenantService.getTenantProfile(tenantPhoneNumber),
    enabled: Boolean(tenantPhoneNumber) && Boolean(user?.token),
  });

  const leasingAgreementsQuery = useQuery({
    queryKey: ["tenant-leasing-agreements", tenantPhoneNumber],
    queryFn: async () => tenantService.listLeasingAgreements(tenantPhoneNumber),
    enabled: Boolean(tenantPhoneNumber) && Boolean(user?.token),
  });

  const [marketplaceStep, setMarketplaceStep] = useState<
    null | "plans" | "payment" | "form"
  >(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [formData, setFormData] = useState({ shopName: "", storeAddress: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [registrationCategories, setRegistrationCategories] = useState<any[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedMarketId, setSelectedMarketId] = useState("");
  const [loadingRegistrationCategories, setLoadingRegistrationCategories] =
    useState(false);
  const [registrationError, setRegistrationError] = useState("");
  const [marketplaceStatus, setMarketplaceStatus] = useState<
    "idle" | "checking" | "pending"
  >("idle");
  const [marketplaceNotice, setMarketplaceNotice] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [openingAgreementId, setOpeningAgreementId] = useState("");
  const [openingReceiptKey, setOpeningReceiptKey] = useState("");

  const tenantData = useMemo(
    () => normalizeTenantProfile(tenantProfileQuery.data, currentProfiler || user),
    [currentProfiler, tenantProfileQuery.data, user],
  );
  const leasingAgreements = useMemo(
    () => normalizeLeasingAgreements(leasingAgreementsQuery.data),
    [leasingAgreementsQuery.data],
  );
  const loading =
    Boolean(user?.token) &&
    (tenantProfileQuery.isLoading ||
      (currentProfilerQuery.isLoading && !tenantPhoneNumber));
  const error = !tenantPhoneNumber && Boolean(user?.token) && !currentProfilerQuery.isLoading
    ? "Tenant phone number is missing on this profiler account."
    : tenantProfileQuery.error
      ? getApiErrorMessage(tenantProfileQuery.error, "Failed to fetch tenant data")
      : currentProfilerQuery.error && !tenantPhoneNumber
        ? getApiErrorMessage(
            currentProfilerQuery.error,
            "Could not resolve the current profiler account.",
          )
        : "";
  const agreementsError = leasingAgreementsQuery.error
    ? getApiErrorMessage(
        leasingAgreementsQuery.error,
        "Could not load leasing agreements.",
      )
    : "";

  const properties = tenantData?.tenancies || [];
  const selectedMarket =
    selectedPlan?.markets?.find((market) => market.id === selectedMarketId) ||
    null;

  const isCheckingMarketplaceAccess = marketplaceStatus === "checking";
  const isPendingApproval =
    !hasMarketplaceAccount &&
    (marketplaceStatus === "pending" ||
      pendingSellerRequest?.profilerId === user?.id ||
      hasSubmittedSellerRequest);

  useEffect(() => {
    if (hasMarketplaceAccount) {
      clearPendingSellerRequest();
      clearPendingPlanSelection();
      setMarketplaceStatus("idle");
      setMarketplaceNotice("This tenant seller account is now approved.");
      return;
    }

    if (pendingSellerRequest?.profilerId === user?.id || hasSubmittedSellerRequest) {
      setMarketplaceStatus("pending");
      setMarketplaceNotice(
        requestStatus === "approved"
          ? "Your seller request has been approved. Seller tools unlock automatically once your seller account is provisioned."
          : "Your seller request has been submitted. Seller tools unlock automatically once your seller account is provisioned.",
      );
      return;
    }

    setMarketplaceStatus("idle");
    setMarketplaceNotice("");
  }, [
    hasMarketplaceAccount,
    hasSubmittedSellerRequest,
    pendingSellerRequest?.profilerId,
    requestStatus,
    user?.id,
  ]);

  useEffect(() => {
    if (!isPendingApproval || !user?.token) {
      return;
    }

    let isActive = true;

    const pollSellerState = async () => {
      try {
        const profile = await profilerService.getMe(user.token);
        if (!isActive || !profile?.seller) {
          return;
        }

        clearPendingSellerRequest();
        clearPendingPlanSelection();
        await refreshProfile();
        resetRegistrationState();
        setSelectedPlan(null);
        setMarketplaceStep(null);
        setMarketplaceStatus("idle");
        setMarketplaceNotice(
          "Your seller account is ready. Use the marketplace button to open seller tools.",
        );
      } catch {
        // keep polling quietly until seller is provisioned
      }
    };

    pollSellerState();
    const intervalId = window.setInterval(pollSellerState, 8000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [isPendingApproval, refreshProfile, user?.token]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const resetRegistrationState = () => {
    setFormData({ shopName: "", storeAddress: "", note: "" });
    setRegistrationCategories([]);
    setSelectedCategoryIds([]);
    setSelectedMarketId("");
    setRegistrationError("");
  };

  const hydrateSelectedPlan = async (planLike) => {
    if (!user?.token) {
      throw new Error("Please log in again to continue seller onboarding.");
    }

    const planId = typeof planLike === "string" ? planLike : planLike?.id;
    if (!planId) {
      throw new Error("Plan data is missing. Please re-select a plan.");
    }

    const planDetails = await planService.getPlanById(planId);
    const eligibleMarkets = Array.isArray(planDetails?.markets)
      ? planDetails.markets.filter((market) => market?.id)
      : [];

    if (!eligibleMarkets.length) {
      throw new Error("This plan does not have any eligible markets.");
    }

    const normalizedPlan = {
      ...planDetails,
      markets: eligibleMarkets,
    };

    setSelectedPlan(normalizedPlan);
    return normalizedPlan;
  };

  const loadRegistrationCategories = async (plan, marketId) => {
    if (!user?.token || !plan || !marketId) {
      return null;
    }

    setLoadingRegistrationCategories(true);
    setRegistrationError("");
    setRegistrationCategories([]);
    setSelectedCategoryIds([]);

    try {
      const selectedPlanMarket = plan?.markets?.find((market) => market.id === marketId);

      if (!selectedPlanMarket) {
        throw new Error("Choose a market that belongs to the selected plan.");
      }

      const data = await marketService.getMarketCategories(marketId);

      setSelectedMarketId(marketId);
      setRegistrationCategories(normalizeMarketCategories(data));
      persistPendingPlanSelection({
        planId: plan.id,
        planName: plan.name,
        planData: plan,
        marketId,
      });
      return marketId;
    } catch (err) {
      setRegistrationError(
        err.message || "Could not load niche categories for this market.",
      );
      setSelectedMarketId(null);
      return null;
    } finally {
      setLoadingRegistrationCategories(false);
    }
  };

  const fetchMarketplaceAccessState = async () => {
    if (!user?.token) {
      return {
        marketplaceState: resolveMarketplaceAccessState({ user }),
        activePlanPayment: null,
        profile: null,
      };
    }

    try {
      const [profile, activePlanPayment] = await Promise.all([
        profilerService.getMe(user.token),
        user?.id ? planService.getActivePlanPayment(user.id).catch(() => null) : null,
      ]);

      return {
        marketplaceState: resolveMarketplaceAccessState({
          user,
          profile,
          activePlanPayment,
        }),
        activePlanPayment,
        profile,
      };
    } catch {
      return {
        marketplaceState: resolveMarketplaceAccessState({ user }),
        activePlanPayment: null,
        profile: null,
      };
    }
  };

  const openSellerRequestFormForPaidPlan = async (activePlanPayment) => {
    const paidPlanId = activePlanPayment?.planId || activePlanPayment?.plan?.id || user?.planId;

    if (!paidPlanId) {
      throw new Error("Your paid plan could not be resolved. Please contact support.");
    }

    resetRegistrationState();
    const fullPlan = await hydrateSelectedPlan(paidPlanId);
    const requestMarketId =
      activePlanPayment?.request?.marketId || activePlanPayment?.request?.market?.id;

    persistPendingPlanSelection({
      planId: fullPlan.id,
      planName: fullPlan.name,
      planData: fullPlan,
      marketId:
        requestMarketId ||
        (fullPlan.markets.length === 1 ? fullPlan.markets[0].id : undefined),
    });

    setMarketplaceStatus("idle");
    setMarketplaceNotice("");
    setMarketplaceStep("form");

    if (requestMarketId) {
      await handleMarketSelection(requestMarketId, fullPlan);
      return;
    }

    if (fullPlan.markets.length === 1) {
      await handleMarketSelection(fullPlan.markets[0].id, fullPlan);
    }
  };

  const handleMarketSelection = async (marketId, planOverride?) => {
    const activePlan = planOverride || selectedPlan;

    if (!activePlan?.id) {
      return;
    }

    await loadRegistrationCategories(activePlan, marketId);
  };

  const handleMarketplaceClick = async () => {
    if (!user?.token) {
      alert("Please log in again to access marketplace plans.");
      return;
    }

    setMarketplaceStatus("checking");
    setMarketplaceNotice("");

    try {
      const {
        marketplaceState,
        activePlanPayment,
        profile,
      } = await fetchMarketplaceAccessState();

      if (profile?.seller) {
        setMarketplaceStatus("idle");
        setMarketplaceNotice("");
        await refreshProfile();
        navigate("/dashboard", { replace: true });
        return;
      }

      const hasSuccessfulPlanPayment = hasActiveSuccessfulPlanPayment(
        activePlanPayment,
      );

      if (hasSuccessfulPlanPayment) {
        const request = activePlanPayment?.request || null;
        const paidRequestStatus = normalizeRequestStatus(request?.status || marketplaceState.requestStatus);

        if (request && paidRequestStatus !== "rejected") {
          clearPendingPlanSelection();
          persistPendingSellerRequest({
            profilerId: user.id,
            planId: activePlanPayment.planId || user?.planId,
            marketId: request?.marketId || request?.market?.id || "",
            submittedAt: request?.updatedAt || request?.createdAt || new Date().toISOString(),
          });
          resetRegistrationState();
          setSelectedPlan(null);
          setMarketplaceStep(null);
          setMarketplaceStatus("pending");
          setMarketplaceNotice(
            paidRequestStatus === "approved"
              ? "Your seller request has been approved. Seller tools unlock automatically once your seller account is provisioned."
              : "Your seller request has been submitted. Seller tools unlock automatically once your seller account is provisioned.",
          );
          return;
        }

        await openSellerRequestFormForPaidPlan(activePlanPayment);
        return;
      }

      if (
        marketplaceState.status === "pending" ||
        hasSubmittedSellerRequest ||
        pendingSellerRequest?.profilerId === user?.id
      ) {
        setMarketplaceStatus("pending");
        setMarketplaceNotice(
          requestStatus === "approved"
            ? "Your seller request has been approved. Seller tools unlock automatically once your seller account is provisioned."
            : "Your seller request has been submitted. Seller tools unlock automatically once your seller account is provisioned.",
        );
        return;
      }

      if (hasPaidPlanWithoutRequest) {
        await openSellerRequestFormForPaidPlan({
          planId: user.planId,
          request: null,
        });
        return;
      }
    } catch (err) {
      console.error("Error checking active plan:", err);
    }

    setMarketplaceStatus("idle");
    setMarketplaceNotice("");
    navigate("/dashboard/pricing");
  };

  const handlePlanSelect = async (plan) => {
    setRegistrationError("");

    try {
      resetRegistrationState();
      const fullPlan = await hydrateSelectedPlan(plan);

      if (fullPlan.isFree || fullPlan.price === 0) {
        setMarketplaceStep("form");

        if (fullPlan.markets.length === 1) {
          await handleMarketSelection(fullPlan.markets[0].id, fullPlan);
        }

        return;
      }

      if (fullPlan.markets.length === 1) {
        setSelectedMarketId(fullPlan.markets[0].id);
      }

      persistPendingPlanSelection({
        planId: fullPlan.id,
        planName: fullPlan.name,
        planData: fullPlan,
        marketId: fullPlan.markets.length === 1 ? fullPlan.markets[0].id : undefined,
      });
      setMarketplaceStep("payment");
    } catch (err) {
      setRegistrationError(err.message || "Could not load the selected plan.");
    }
  };

  const handlePaymentConfirm = async () => {
    if (!selectedPlan || !user?.token) return;

    if (!selectedMarketId) {
      alert("Choose one of the markets covered by this plan before continuing.");
      return;
    }

    try {
      const data = await planService.makePlanPayment({
        planId: selectedPlan.id,
        profilerId: currentProfilerId || user.id,
        durationNumber: 1,
        callbackUrl: `${window.location.origin}/payment/callback`,
      });
      const paymentUrl = data.paymentUrl || data.authorization_url;

      persistPendingPlanSelection({
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        planData: selectedPlan,
        paymentReference: data.reference,
        marketId: selectedMarketId,
      });

      if (paymentUrl) {
        window.location.href = paymentUrl;
        return;
      }

      if (data.reference) {
        window.location.href = `${window.location.origin}/payment/callback?reference=${encodeURIComponent(data.reference)}&planId=${encodeURIComponent(selectedPlan.id)}`;
        return;
      }

      alert("Payment URL not provided by server");
    } catch (err) {
      alert(`Error: ${err.message || "Could not start plan payment."}`);
    }
  };

  const handleOpenReceipt = async (tenancy, payment) => {
    if (!tenantPhoneNumber || !tenancy?.id || !payment?.id) {
      setDocumentError("Receipt details are unavailable for this payment.");
      return;
    }

    const receiptKey = `${tenancy.id}:${payment.id}`;
    setDocumentError("");
    setOpeningReceiptKey(receiptKey);

    try {
      const receiptUrl = tenantService.getReceiptUrl({
        tenancyId: tenancy.id,
        paymentId: payment.id,
        phoneNumber: tenantPhoneNumber,
      });
      const openedWindow = window.open(receiptUrl, "_blank", "noopener,noreferrer");

      if (!openedWindow) {
        throw new Error("Allow pop-ups to open the receipt PDF.");
      }
    } catch (error) {
      setDocumentError(
        getApiErrorMessage(error, "Could not open the payment receipt."),
      );
    } finally {
      setOpeningReceiptKey("");
    }
  };

  const handleOpenAgreement = async (agreement) => {
    if (!tenantPhoneNumber || !agreement?.id) {
      setDocumentError("Leasing agreement details are unavailable.");
      return;
    }

    setDocumentError("");
    setOpeningAgreementId(agreement.id);

    try {
      const responseData = await tenantService.getLeasingAgreement(
        agreement.id,
        tenantPhoneNumber,
      );
      const documentUrl =
        agreement.documentUrl ||
        responseData?.documentUrl ||
        responseData?.agreement?.documentUrl ||
        responseData?.data?.documentUrl ||
        null;

      if (!documentUrl) {
        throw new Error("Leasing agreement PDF is not available yet.");
      }

      const openedWindow = window.open(
        documentUrl,
        "_blank",
        "noopener,noreferrer",
      );

      if (!openedWindow) {
        throw new Error("Allow pop-ups to open the leasing agreement PDF.");
      }
    } catch (error) {
      setDocumentError(
        getApiErrorMessage(error, "Could not open the leasing agreement."),
      );
    } finally {
      setOpeningAgreementId("");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!user?.token) {
      alert("Authentication error. Please log in again.");
      return;
    }

    if (!selectedCategoryIds.length) {
      setRegistrationError("Select at least one niche category.");
      return;
    }

    if (!selectedMarketId) {
      setRegistrationError("Choose a market covered by the selected plan.");
      return;
    }

    setSubmitting(true);
    setRegistrationError("");

    try {
      const plan = selectedPlan;

      if (!plan?.id) {
        alert("Plan data is missing. Please re-select a plan and try again.");
        setSubmitting(false);
        return;
      }

      const maxCategories = Number(plan?.maxCategories ?? 0);
      if (maxCategories > 0 && selectedCategoryIds.length > maxCategories) {
        setRegistrationError(
          `Your plan allows a maximum of ${maxCategories} niche categories.`,
        );
        setSubmitting(false);
        return;
      }

      const selectedPlanMarket = plan?.markets?.find(
        (market) => market.id === selectedMarketId,
      );

      if (!selectedPlanMarket) {
        setRegistrationError("Choose a market that belongs to the selected plan.");
        setSubmitting(false);
        return;
      }

      const requestBody = {
        profilerId: user.id,
        marketId: selectedPlanMarket.id,
        planId: plan.id,
        shopName: formData.shopName,
        storeAddress: formData.storeAddress,
        note: formData.note || "",
        nicheCategoryIds: selectedCategoryIds,
      };

      try {
        await marketService.createSellerRequest(requestBody);
      } catch (requestError) {
        const errorData = requestError?.response?.data || {};
        const duplicateRequestState = getDuplicateRequestResolution(
          errorData?.message || errorData?.error,
        );

        if (duplicateRequestState === "approved") {
          clearPendingPlanSelection();
          clearPendingSellerRequest();
          resetRegistrationState();
          setMarketplaceStep(null);
          setSelectedPlan(null);
          setMarketplaceStatus("idle");
          setMarketplaceNotice("");
          setSubmitting(false);
          await refreshProfile();
          navigate("/dashboard", { replace: true });
          return;
        }

        if (duplicateRequestState === "pending") {
          clearPendingPlanSelection();
          persistPendingSellerRequest({
            profilerId: user.id,
            planId: plan.id,
            marketId: selectedPlanMarket.id,
            submittedAt: new Date().toISOString(),
          });
          resetRegistrationState();
          setMarketplaceStep(null);
          setSelectedPlan(null);
          setMarketplaceStatus("pending");
          setMarketplaceNotice(
            "Your seller request has been submitted. Seller tools unlock automatically once your seller account is provisioned.",
          );
          setSubmitting(false);
          return;
        }

        setRegistrationError(
          getApiErrorMessage(
            requestError,
            "Could not submit marketplace registration.",
          ),
        );
        setSubmitting(false);
        return;
      }

      clearPendingPlanSelection();
      persistPendingSellerRequest({
        profilerId: user.id,
        planId: plan.id,
        marketId: selectedPlanMarket.id,
        submittedAt: new Date().toISOString(),
      });
      setMarketplaceStatus("pending");
      setMarketplaceNotice(
        "Your seller request has been submitted. Seller tools unlock automatically once your seller account is provisioned.",
      );

      resetRegistrationState();
      setMarketplaceStep(null);
      setSelectedPlan(null);
      setSubmitting(false);
    } catch (err) {
      setRegistrationError(err.message || "Could not submit marketplace registration.");
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const closePlans = () => {
    setMarketplaceStep(null);
  };
  const closePayment = () => {
    clearPendingPlanSelection();
    setMarketplaceStep(null);
    setSelectedPlan(null);
    resetRegistrationState();
  };
  const closeForm = () => {
    clearPendingPlanSelection();
    setMarketplaceStep(null);
    setSelectedPlan(null);
    resetRegistrationState();
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-gray-950 text-gray-100" : "bg-gray-50 text-gray-900"
        }`}>
      {/* ── Navbar ── */}
      <header
        className={`sticky top-0 z-30 backdrop-blur-md border-b transition-colors duration-300 ${isDark
            ? "bg-gray-900/90 border-gray-800"
            : "bg-white/95 border-gray-200 shadow-sm"
          }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Store size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">ShopOwnaa</p>
              <p
                className={`text-xs truncate font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Tenant Property Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleMarketplaceClick}
              disabled={isCheckingMarketplaceAccess}
              className={`hidden sm:flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl transition-colors active:scale-95 ${isPendingApproval
                  ? "bg-amber-500/10 text-amber-700 border border-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20"
                  : "bg-violet-600 hover:bg-violet-500 text-white"
                } disabled:cursor-not-allowed disabled:opacity-70`}>
              {isCheckingMarketplaceAccess ? (
                <RefreshCw size={13} className="animate-spin" />
              ) : (
                <ShoppingBag size={13} />
              )}
              {isCheckingMarketplaceAccess
                ? "Checking Access..."
                : isPendingApproval
                  ? "Seller Request Pending"
                  : hasPaidPlanWithoutRequest
                    ? "Complete Seller Request"
                  : hasMarketplaceAccount
                    ? "Go to Seller Dashboard"
                    : "Open Marketplace Platform"}
              {!isCheckingMarketplaceAccess && !isPendingApproval && (
                <ArrowRight size={13} />
              )}
            </button>

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-xl transition-colors ${isDark
                  ? "text-gray-400 hover:bg-gray-800"
                  : "text-gray-500 hover:bg-gray-100"
                }`}>
              {isDark ? (
                <Sun size={18} className="text-amber-400" />
              ) : (
                <Moon size={18} className="text-indigo-400" />
              )}
            </button>

            <button
              onClick={handleLogout}
              className={`p-2 rounded-xl transition-colors ${isDark
                  ? "text-gray-400 hover:text-rose-400 hover:bg-rose-500/10"
                  : "text-gray-500 hover:text-rose-500 hover:bg-rose-50"
                }`}>
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <TenantProfileHeader
          user={user}
          tenantData={tenantData}
          isDark={isDark}
        />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2
              className={`text-lg font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              My Leasing
            </h2>
            <p
              className={`text-sm mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {loading ? "Loading..." : properties.length}{" "}
              {properties.length === 1 ? "active lease" : "active leases"} found
            </p>
          </div>
        </div>

        {/* Mobile CTA */}
        <button
          onClick={handleMarketplaceClick}
          disabled={isCheckingMarketplaceAccess}
          className={`sm:hidden w-full flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-xl transition-colors active:scale-95 ${isPendingApproval
              ? "bg-amber-500/10 text-amber-700 border border-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20"
              : "bg-violet-600 hover:bg-violet-500 text-white"
            } disabled:cursor-not-allowed disabled:opacity-70`}>
          {isCheckingMarketplaceAccess ? (
            <RefreshCw size={15} className="animate-spin" />
          ) : (
            <ShoppingBag size={15} />
          )}
          {isCheckingMarketplaceAccess
            ? "Checking Access..."
            : isPendingApproval
              ? "Seller Request Pending"
              : hasPaidPlanWithoutRequest
                ? "Complete Seller Request"
              : hasMarketplaceAccount
                ? "Go to Seller Dashboard"
                : "Open Marketplace Platform"}
          {!isCheckingMarketplaceAccess && !isPendingApproval && (
            <ArrowRight size={15} />
          )}
        </button>

        {(isPendingApproval || marketplaceNotice) && (
          <div
            className={`rounded-2xl border p-5 ${isDark
                ? "bg-amber-500/10 border-amber-500/20"
                : "bg-amber-50 border-amber-200"
              }`}>
            <p
              className={`text-sm font-semibold ${isDark ? "text-amber-300" : "text-amber-800"
                }`}>
              {hasMarketplaceAccount
                ? "Seller Account Approved"
                : isPendingApproval
                  ? "Seller Request Pending"
                  : "Marketplace Update"}
            </p>
            <p
              className={`mt-1 text-sm ${isDark ? "text-amber-400" : "text-amber-700"
                }`}>
              {marketplaceNotice ||
                "Your seller request has been submitted. Seller tools unlock automatically once your seller account is provisioned."}
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            className={`rounded-2xl border p-5 ${isDark ? "bg-rose-500/10 border-rose-500/20" : "bg-rose-50 border-rose-200"}`}>
            <p className={isDark ? "text-rose-300" : "text-rose-700"}>
              {error}
            </p>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div
            className={`rounded-2xl border p-12 text-center ${isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}>
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin w-8 h-8 text-violet-600"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p
              className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              Loading your tenant profile...
            </p>
          </div>
        ) : properties.length === 0 ? (
          <div
            className={`rounded-2xl border p-12 text-center ${isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}>
            <Building2
              size={40}
              className={`mx-auto mb-4 ${isDark ? "text-gray-700" : "text-gray-300"}`}
            />
            <p
              className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              No active property leases found.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {properties.map((prop, index) => (
              <PropertyCard
                key={prop.id}
                prop={prop}
                agreement={findAgreementForTenancy(prop, leasingAgreements)}
                isDark={isDark}
                index={index}
                onOpenAgreement={handleOpenAgreement}
                onOpenReceipt={handleOpenReceipt}
                openingAgreementId={openingAgreementId}
                openingReceiptKey={openingReceiptKey}
              />
            ))}
          </div>
        )}

        {documentError && (
          <div
            className={`rounded-2xl border p-4 text-sm ${isDark ? "bg-rose-500/10 border-rose-500/20 text-rose-200" : "bg-rose-50 border-rose-200 text-rose-700"}`}>
            {documentError}
          </div>
        )}

        <LeasingAgreementsSection
          agreements={leasingAgreements}
          error={agreementsError}
          isDark={isDark}
          loading={Boolean(tenantPhoneNumber) && leasingAgreementsQuery.isLoading}
          onOpenAgreement={handleOpenAgreement}
          openingAgreementId={openingAgreementId}
        />
      </main>

      {/* ── Modals ── */}
      <Modal
        isOpen={marketplaceStep === "plans"}
        onClose={closePlans}
        title="Choose Your Marketplace Plan"
        size="xl">
        <PlanSelectionContent
          isDark={isDark}
          token={user?.token}
          onSelect={handlePlanSelect}
        />
      </Modal>

      <Modal
        isOpen={marketplaceStep === "payment"}
        onClose={closePayment}
        title={`Payment · ${selectedPlan?.name ?? ""} Plan`}
        size="md">
        <PaymentScreenContent
          isDark={isDark}
          selectedPlan={selectedPlan}
          selectedMarketId={selectedMarketId}
          markets={selectedPlan?.markets || []}
          onMarketChange={(marketId) => setSelectedMarketId(marketId)}
          onConfirm={handlePaymentConfirm}
          onBack={() => setMarketplaceStep("plans")}
        />
      </Modal>

      <Modal
        isOpen={marketplaceStep === "form"}
        onClose={closeForm}
        title={`Marketplace Registration · ${selectedPlan?.name ?? ""}`}
        size="lg">
        <RegistrationFormContent
          isDark={isDark}
          selectedPlan={selectedPlan}
          markets={selectedPlan?.markets || []}
          selectedMarket={selectedMarket}
          selectedMarketId={selectedMarketId}
          formData={formData}
          categories={registrationCategories}
          selectedCategoryIds={selectedCategoryIds}
          onMarketChange={(marketId) => handleMarketSelection(marketId)}
          onToggleCategory={(categoryId) => {
            const maxCategories = Number(selectedPlan?.maxCategories ?? 0);
            setRegistrationError("");
            setSelectedCategoryIds((prev) => {
              if (prev.includes(categoryId)) {
                return prev.filter((id) => id !== categoryId);
              }

              if (maxCategories > 0 && prev.length >= maxCategories) {
                setRegistrationError(
                  `Your plan allows a maximum of ${maxCategories} niche categories.`,
                );
                return prev;
              }

              return [...prev, categoryId];
            });
          }}
          loadingCategories={loadingRegistrationCategories}
          registrationError={registrationError}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          onBack={() => setMarketplaceStep("plans")}
          submitting={submitting}
        />
      </Modal>
    </div>
  );
}

// ── Tenant Profile Header ────────────────────────────────────────────────────

function TenantProfileHeader({ user, tenantData, isDark }) {
  if (!user) return null;

  const gradientColors = [
    "from-violet-500 via-indigo-500 to-purple-600",
    "from-amber-500 via-orange-500 to-rose-500",
    "from-emerald-500 via-teal-500 to-cyan-500",
  ];
  const bgGradient =
    gradientColors[(user.id?.charCodeAt(0) || 0) % gradientColors.length];

  const firstName =
    tenantData?.firstName || user.firstName || user.name?.split(" ")[0] || "T";
  const lastName =
    tenantData?.lastName || user.lastName || user.name?.split(" ")[1] || "P";
  const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-colors duration-300 ${isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200 shadow-sm"}`}>
      <div className="px-5 sm:px-7 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="flex items-end gap-4">
            <div
              className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br ${bgGradient} flex items-center justify-center text-white text-2xl sm:text-3xl font-black border-4 shadow-lg flex-shrink-0 ${isDark ? "border-gray-900" : "border-white"}`}>
              {initials}
            </div>
            <div className="hidden sm:block pb-1">
              <div className="flex items-center gap-2">
                <h1
                  className={`text-xl font-black bg-gradient-to-r ${bgGradient} bg-clip-text text-transparent`}>
                  {firstName} {lastName}
                </h1>
                {user.verified && (
                  <BadgeCheck
                    size={18}
                    className="text-violet-400 flex-shrink-0"
                  />
                )}
              </div>
              <p
                className={`text-sm font-medium mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Tenant Account
              </p>
            </div>
          </div>
          <div className="sm:pb-1">
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${user.plan === "Premium"
                  ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                  : user.plan === "Advanced"
                    ? "bg-violet-500/15 text-violet-400 border-violet-500/30"
                    : "bg-gray-500/15 text-gray-400 border-gray-500/30"
                }`}>
              {user.plan === "Premium" && <Crown size={11} />}
              {user.plan === "Advanced" && <Sparkles size={11} />}
              {user.plan || "Tenant"} Account
            </span>
          </div>
        </div>

        {/* Name row – mobile */}
        <div className="mt-3 sm:hidden">
          <div className="flex items-center gap-2">
            <h1
              className={`text-lg font-black bg-gradient-to-r ${bgGradient} bg-clip-text text-transparent`}>
              {firstName} {lastName}
            </h1>
            {user.verified && (
              <BadgeCheck size={16} className="text-violet-400 flex-shrink-0" />
            )}
          </div>
          <p
            className={`text-sm font-medium mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Tenant Account
          </p>
        </div>

        <div
          className={`mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {user.email && (
            <span className="flex items-center gap-1.5">
              <Mail size={14} className="text-violet-400 flex-shrink-0" />
              {user.email}
            </span>
          )}
          {(user.phoneNumber || user.phone) && (
            <span className="flex items-center gap-1.5">
              <Phone size={14} className="text-violet-400 flex-shrink-0" />
              {user.phoneNumber || user.phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Property Card ─────────────────────────────────────────────────────────────

function PropertyCard({
  prop,
  agreement,
  isDark,
  index,
  onOpenAgreement,
  onOpenReceipt,
  openingAgreementId,
  openingReceiptKey,
}) {
  const days = daysRemaining(prop.rentEndDate);
  const payments = Array.isArray(prop.payments) ? prop.payments : [];
  const latestPayment = prop.latestPayment || payments[0] || null;
  const canOpenAgreement = Boolean(agreement?.id);
  const canOpenLatestReceipt = Boolean(prop.id && latestPayment?.id);
  const isOpeningAgreement = canOpenAgreement && openingAgreementId === agreement.id;
  const isOpeningLatestReceipt =
    canOpenLatestReceipt && openingReceiptKey === `${prop.id}:${latestPayment.id}`;

  let status = "active";
  if (Number.isFinite(days) && days < 0) status = "expired";
  else if (Number.isFinite(days) && days < 30 && prop.paymentSummary?.paymentRequired)
    status = "due_soon";

  const statusConfig = {
    active: {
      label: "Active",
      color: "text-emerald-400",
      bg: isDark ? "bg-emerald-500/10" : "bg-emerald-50",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
    due_soon: {
      label: "Due Soon",
      color: "text-amber-400",
      bg: isDark ? "bg-amber-500/10" : "bg-amber-50",
      border: "border-amber-500/20",
      dot: "bg-amber-400",
    },
    expired: {
      label: "Expired",
      color: "text-rose-400",
      bg: isDark ? "bg-rose-500/10" : "bg-rose-50",
      border: "border-rose-500/20",
      dot: "bg-rose-400",
    },
  };
  const statusInfo = statusConfig[status];

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-colors duration-300 ${isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200 shadow-sm"}`}>
      <div
        className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isDark ? "border-gray-800 bg-gray-800/30" : "border-gray-100 bg-gray-50"}`}>
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border border-violet-500/20 ${isDark ? "bg-violet-500/10" : "bg-violet-50"}`}>
            <Building2 size={20} className="text-violet-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-sm">
                {prop.property?.name || "Property"}
              </h2>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"}`}>
                Property {index + 1}
              </span>
            </div>
            <p
              className={`text-xs mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {prop.property?.locationAddress}, {prop.property?.locationState}
            </p>
          </div>
        </div>
        <span
          className={`self-start sm:self-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${statusInfo.bg} ${statusInfo.border} ${statusInfo.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />
          {statusInfo.label}
        </span>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <InfoItem
            label="Rent Amount"
            value={formatCurrency(prop.rentAmount)}
            isDark={isDark}
            highlight
          />
          <InfoItem label="Plan" value={prop.paymentPlan} isDark={isDark} />
          <InfoItem
            label="Rent Start"
            value={formatDate(prop.rentStartDate)}
            isDark={isDark}
          />
          <InfoItem
            label="Rent End"
            value={formatDate(prop.rentEndDate)}
            isDark={isDark}
          />
          <InfoItem
            label="Last Payment"
            value={prop.lastPaymentAt ? formatDate(prop.lastPaymentAt) : "N/A"}
            isDark={isDark}
          />
          <DaysRemainingItem days={days} isDark={isDark} />
        </div>

        {prop.paymentSummary && (
          <>
            <div
              className={`border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoItem
                label="Total Due"
                value={formatCurrency(prop.paymentSummary.totalDue)}
                isDark={isDark}
                highlight={prop.paymentSummary.totalDue > 0}
              />
              <InfoItem
                label="Total Paid"
                value={formatCurrency(prop.paymentSummary.totalPaidAmount)}
                isDark={isDark}
              />
              <InfoItem
                label="Outstanding"
                value={formatCurrency(prop.paymentSummary.outstandingAmount)}
                isDark={isDark}
              />
              <InfoItem
                label="Payment Status"
                value={prop.paymentSummary.paymentRequired ? "Due" : "Paid"}
                isDark={isDark}
              />
            </div>
          </>
        )}

        <div
          className={`border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}
        />

        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold">Payment History</p>
            <p
              className={`text-xs mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {payments.length > 0
                ? `${payments.length} recorded payment${payments.length === 1 ? "" : "s"}`
                : "No payment records were returned for this tenancy yet."}
            </p>
          </div>

          {payments.length > 0 ? (
            <div className="space-y-2">
              {payments.slice(0, 3).map((payment) => {
                const receiptKey = `${prop.id}:${payment.id}`;
                const isOpeningReceipt = openingReceiptKey === receiptKey;

                return (
                  <div
                    key={payment.id}
                    className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDark ? "border-gray-800 bg-gray-800/40" : "border-gray-100 bg-gray-50"}`}>
                    <div>
                      <p className="text-sm font-semibold">
                        {formatCurrency(payment.amount)}
                      </p>
                      <p
                        className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {formatDate(payment.paidAt)} • {payment.status || "Recorded"}
                        {payment.transactionReference
                          ? ` • Ref ${payment.transactionReference}`
                          : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onOpenReceipt(prop, payment)}
                      disabled={!payment.id || isOpeningReceipt}
                      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold transition-colors ${!payment.id ? "cursor-not-allowed opacity-60" : isDark ? "bg-gray-700 hover:bg-gray-600 text-gray-100" : "bg-white hover:bg-gray-100 text-gray-700 border border-gray-200"}`}>
                      {isOpeningReceipt ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      Open Receipt
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${isDark ? "border-gray-800 bg-gray-800/30 text-gray-400" : "border-gray-100 bg-gray-50 text-gray-500"}`}>
              Receipt buttons appear here once payment records are returned by the tenant portal.
            </div>
          )}
        </div>

        <div
          className={`border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}
        />

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => latestPayment && onOpenReceipt(prop, latestPayment)}
            disabled={!canOpenLatestReceipt || isOpeningLatestReceipt}
            className={`flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors border active:scale-95 ${!canOpenLatestReceipt ? "cursor-not-allowed opacity-60" : ""} ${isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-200" : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"}`}>
            {isOpeningLatestReceipt ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            {canOpenLatestReceipt ? "Open Latest Receipt" : "No Receipt Available"}
          </button>
          <button
            type="button"
            onClick={() => agreement && onOpenAgreement(agreement)}
            disabled={!canOpenAgreement || isOpeningAgreement}
            className={`flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors border active:scale-95 ${!canOpenAgreement ? "cursor-not-allowed opacity-60" : ""} ${isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-200" : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"}`}>
            {isOpeningAgreement ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <FileText size={16} />
            )}
            {canOpenAgreement ? "Open Leasing Agreement" : "Agreement Unavailable"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeasingAgreementsSection({
  agreements,
  error,
  isDark,
  loading,
  onOpenAgreement,
  openingAgreementId,
}) {
  return (
    <section
      className={`rounded-2xl border p-6 ${isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200 shadow-sm"}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2
            className={`text-lg font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
            Leasing Agreements
          </h2>
          <p
            className={`text-sm mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Open the agreement PDF directly from the tenant portal response.
          </p>
        </div>
        <span
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
          {loading ? "Loading..." : `${agreements.length} agreement${agreements.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {error && (
        <div
          className={`mt-5 rounded-xl border p-4 text-sm ${isDark ? "bg-rose-500/10 border-rose-500/20 text-rose-200" : "bg-rose-50 border-rose-200 text-rose-700"}`}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-6 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
          <RefreshCw size={16} className="animate-spin" />
          Loading leasing agreements...
        </div>
      ) : agreements.length === 0 ? (
        <div
          className={`mt-6 rounded-xl border p-4 text-sm ${isDark ? "border-gray-800 bg-gray-800/30 text-gray-400" : "border-gray-100 bg-gray-50 text-gray-500"}`}>
          No leasing agreements were returned for this tenant phone number.
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {agreements.map((agreement) => {
            const isOpening = openingAgreementId === agreement.id;

            return (
              <div
                key={agreement.id}
                className={`rounded-xl border px-4 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${isDark ? "border-gray-800 bg-gray-800/30" : "border-gray-100 bg-gray-50"}`}>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold">{agreement.name}</p>
                    <span
                      className={`text-[11px] font-semibold px-2 py-1 rounded-full ${isDark ? "bg-gray-700 text-gray-300" : "bg-white text-gray-500 border border-gray-200"}`}>
                      {agreement.status}
                    </span>
                  </div>
                  <p
                    className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {agreement.propertyName}
                    {agreement.startDate ? ` • Starts ${formatDate(agreement.startDate)}` : ""}
                    {agreement.endDate ? ` • Ends ${formatDate(agreement.endDate)}` : ""}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => onOpenAgreement(agreement)}
                  disabled={isOpening || !agreement.id}
                  className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${!agreement.id ? "cursor-not-allowed opacity-60" : isDark ? "bg-gray-700 hover:bg-gray-600 text-gray-100" : "bg-white hover:bg-gray-100 text-gray-700 border border-gray-200"}`}>
                  {isOpening ? (
                    <RefreshCw size={15} className="animate-spin" />
                  ) : (
                    <FileText size={15} />
                  )}
                  Open Agreement
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function InfoItem({ label, value, isDark, highlight }: {
  label: string,
  value: string,
  isDark: boolean,
  highlight?: boolean
}) {
  return (
    <div
      className={`p-3.5 rounded-xl ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}>
      <p
        className={`text-xs mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
        {label}
      </p>
      <p
        className={`text-sm font-semibold ${highlight ? "text-violet-400" : isDark ? "text-gray-100" : "text-gray-900"}`}>
        {value}
      </p>
    </div>
  );
}

function DaysRemainingItem({ days, isDark }) {
  if (!Number.isFinite(days)) {
    return (
      <div
        className={`p-3.5 rounded-xl ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}>
        <p
          className={`text-xs mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
          Days Remaining
        </p>
        <p
          className={`text-sm font-semibold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
          Unavailable
        </p>
      </div>
    );
  }

  const isExpired = days < 0;
  const isUrgent = !isExpired && days < 30;
  return (
    <div
      className={`p-3.5 rounded-xl ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}>
      <p
        className={`text-xs mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
        Days Remaining
      </p>
      <p
        className={`text-sm font-semibold ${isExpired ? "text-rose-400" : isUrgent ? "text-amber-400" : isDark ? "text-gray-100" : "text-gray-900"}`}>
        {isExpired ? "Expired" : `${days} days`}
      </p>
    </div>
  );
}

// ── Plan Selection ────────────────────────────────────────────────────────────

function PlanSelectionContent({ isDark, onSelect, token }) {
  const plansQuery = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const data = await planService.listPlans();
      return Array.isArray(data) ? data : data?.plans || [];
    },
    enabled: Boolean(token),
  });

  const plans = plansQuery.data || [];
  const loading = Boolean(token) && plansQuery.isLoading;
  const error = !token
    ? "Please log in to access marketplace plans."
    : plansQuery.error
      ? getApiErrorMessage(plansQuery.error, "Could not load marketplace plans.")
      : null;

  function getAccent(plan) {
    if (plan.isFree || plan.price === 0) return "gray";
    if (plan.price <= 20000) return "violet";
    return "amber";
  }

  function getFeatures(plan) {
    return [
      {
        text: `Up to ${plan.maxProducts} product listing${plan.maxProducts === 1 ? "" : "s"}`,
        included: true,
      },
      {
        text: `${plan.maxCategories >= 999 ? "All" : plan.maxCategories} market categor${plan.maxCategories === 1 ? "y" : "ies"}`,
        included: true,
      },
      {
        text: `${plan.markets?.length || 0} market${(plan.markets?.length || 0) === 1 ? "" : "s"} available`,
        included: !!plan.markets?.length,
      },
      {
        text: plan.isFree ? "Standard support" : "Priority support",
        included: true,
      },
    ];
  }

  if (loading) {
    return (
      <div className="py-12 text-center space-y-4">
        <svg
          className="animate-spin w-8 h-8 text-violet-600 mx-auto"
          viewBox="0 0 24 24"
          fill="none">
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="30 70"
            strokeLinecap="round"
          />
        </svg>
        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          Loading available plans...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`p-4 rounded-xl space-y-3 ${isDark ? "bg-rose-500/10 border border-rose-500/25" : "bg-rose-50 border border-rose-200"}`}>
        <p
          className={`text-sm font-semibold ${isDark ? "text-rose-300" : "text-rose-700"}`}>
          ⚠️ Error Loading Plans
        </p>
        <p
          className={`text-xs ${isDark ? "text-rose-200/80" : "text-rose-600"}`}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${isDark ? "bg-rose-600/40 hover:bg-rose-600/60 text-rose-200" : "bg-rose-600 hover:bg-rose-700 text-white"}`}>
          🔄 Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p
        className={`text-sm text-center mb-5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        Select a plan to access the ShopOwnaa Marketplace.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const accent = getAccent(plan);
          const a = PLAN_ACCENT[accent];
          const features = getFeatures(plan);
          const isPopular = accent === "violet";

          return (
            <div
              key={plan.id}
              onClick={() => onSelect(plan)}
              className={`relative rounded-2xl border overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${isDark ? "bg-gray-800 border-gray-700 hover:border-gray-500" : "bg-white border-gray-200 hover:border-gray-300"}`}>
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-violet-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div
                className={`bg-gradient-to-br ${a.header} px-5 pt-5 pb-4 border-b ${isDark ? "border-gray-700" : "border-gray-100"}`}>
                <div className="flex items-center gap-2.5 mb-3">
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center ${isDark ? "bg-gray-800/60" : "bg-white/70"} border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                    {plan.isFree ? (
                      <Zap size={18} className="text-gray-400" />
                    ) : accent === "violet" ? (
                      <Sparkles size={18} className="text-violet-400" />
                    ) : (
                      <Crown size={18} className="text-amber-400" />
                    )}
                  </div>
                  <h3
                    className={`text-sm font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                    {plan.name}
                  </h3>
                </div>
                <div className="flex items-baseline gap-1">
                  {plan.isFree || plan.price === 0 ? (
                    <span
                      className={`text-2xl font-black ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                      Free
                    </span>
                  ) : (
                    <>
                      <span
                        className={`text-xs font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        ₦
                      </span>
                      <span
                        className={`text-2xl font-black ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                        {plan.price.toLocaleString()}
                      </span>
                      <span
                        className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        /month
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="px-5 py-4 space-y-2">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {f.included ? (
                      <Check
                        size={13}
                        className="text-emerald-400 flex-shrink-0 mt-0.5"
                      />
                    ) : (
                      <X
                        size={13}
                        className={`flex-shrink-0 mt-0.5 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                      />
                    )}
                    <span
                      className={`text-xs ${f.included ? (isDark ? "text-gray-300" : "text-gray-700") : isDark ? "text-gray-600" : "text-gray-400"}`}>
                      {f.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Market name badge */}
              {plan.markets?.[0] && (
                <div className="px-5 pb-3">
                  <span
                    className={`text-[10px] font-semibold px-2 py-1 rounded-full ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                    📍 {plan.markets?.[0]?.name}, {plan.markets?.[0]?.city}
                  </span>
                </div>
              )}

              <div className="px-5 pb-5">
                <button
                  className={`w-full py-2.5 rounded-xl text-xs font-bold transition-all duration-200 active:scale-95 ${a.btn}`}>
                  Select {plan.name}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Payment Screen ────────────────────────────────────────────────────────────

function PaymentScreenContent({
  isDark,
  selectedPlan,
  selectedMarketId,
  markets,
  onMarketChange,
  onConfirm,
  onBack,
}) {
  const [processing, setProcessing] = useState(false);

  const handleConfirm = async () => {
    setProcessing(true);
    await onConfirm();
    // If onConfirm redirects to Paystack, this line won't be reached
    setProcessing(false);
  };

  return (
    <div className="space-y-6">
      <div
        className={`rounded-2xl border p-5 ${isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            {selectedPlan?.price > 20000 ? (
              <Crown size={20} className="text-amber-400" />
            ) : (
              <Zap size={20} className="text-violet-400" />
            )}
          </div>
          <div>
            <p
              className={`font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              {selectedPlan?.name} Plan
            </p>
            <p
              className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              ₦{selectedPlan?.price?.toLocaleString()}/month
            </p>
          </div>
        </div>
        <ul className="space-y-2">
          <li
            className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            <Check size={14} className="text-violet-400 flex-shrink-0" />
            Up to {selectedPlan?.maxProducts} product listings
          </li>
          <li
            className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            <Check size={14} className="text-violet-400 flex-shrink-0" />
            {selectedPlan?.maxCategories >= 999
              ? "All"
              : selectedPlan?.maxCategories}{" "}
            market categories
          </li>
          {selectedPlan?.markets?.[0] && (
            <li
              className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              <Check size={14} className="text-violet-400 flex-shrink-0" />
              Market: {selectedPlan?.markets?.[0]?.name},{" "}
              {selectedPlan?.markets?.[0]?.city}
            </li>
          )}
        </ul>
      </div>

      <div
        className={`rounded-2xl border p-5 ${isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
        <label className={`block text-xs font-semibold uppercase tracking-wide mb-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          Eligible market
        </label>
        <select
          value={selectedMarketId}
          onChange={(event) => onMarketChange(event.target.value)}
          className={`mb-4 w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-violet-500/50 ${isDark
            ? "bg-gray-800 border-gray-700 text-gray-100"
            : "bg-white border-gray-200 text-gray-900"
            }`}>
          <option value="">Choose a market covered by this plan</option>
          {markets.map((market) => (
            <option key={market.id} value={market.id}>
              {market.name}{market.city ? `, ${market.city}` : ""}
            </option>
          ))}
        </select>

        <p
          className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
          Payment Summary
        </p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={isDark ? "text-gray-400" : "text-gray-500"}>
              {selectedPlan?.name} Plan
            </span>
            <span
              className={`font-semibold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              ₦{selectedPlan?.price?.toLocaleString()}
            </span>
          </div>
          <div
            className={`border-t pt-2 flex justify-between text-sm font-bold ${isDark ? "border-gray-700 text-gray-100" : "border-gray-200 text-gray-900"}`}>
            <span>Total</span>
            <span>₦{selectedPlan?.price?.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div
        className={`rounded-xl p-3 text-xs ${isDark ? "bg-blue-500/10 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
        <p className="font-semibold mb-1">💳 Secure Payment via Paystack</p>
        <p>
          You will be redirected to Paystack to complete your payment securely.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={processing}
          className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${processing ? "opacity-50 cursor-not-allowed" : isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300" : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"}`}>
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={processing || !selectedMarketId}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${processing ? "bg-violet-600/50 text-white/60 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-500 text-white"}`}>
          {processing ? (
            <>
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
              Redirecting to Paystack…
            </>
          ) : (
            <>
              <CreditCard size={15} />
              Confirm Payment
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Registration Form ─────────────────────────────────────────────────────────

function RegistrationFormContent({
  isDark,
  selectedPlan,
  markets,
  selectedMarket,
  selectedMarketId,
  formData,
  categories,
  selectedCategoryIds,
  onMarketChange,
  onToggleCategory,
  loadingCategories,
  registrationError,
  onChange,
  onSubmit,
  onBack,
  submitting,
}) {
  const inputCls = `w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-violet-500/50 ${isDark
      ? "bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-600"
      : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white"
    }`;
  const labelCls = `block text-xs font-semibold mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`;
  const maxCategories = Number(selectedPlan?.maxCategories ?? 0);
  const categoryLimitReached =
    maxCategories > 0 && selectedCategoryIds.length >= maxCategories;
  const isValid =
    selectedMarketId &&
    formData.shopName.trim() &&
    formData.storeAddress.trim() &&
    selectedCategoryIds.length > 0;

  const marketAddress = selectedMarket?.city || "";

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {selectedPlan && (
        <div
          className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${isDark ? "bg-violet-500/10 border-violet-500/20 text-violet-300" : "bg-violet-50 border-violet-200 text-violet-700"}`}>
          <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            {selectedPlan.isFree ? (
              <Zap size={14} className="text-gray-400" />
            ) : selectedPlan.price <= 20000 ? (
              <Sparkles size={14} className="text-violet-400" />
            ) : (
              <Crown size={14} className="text-amber-400" />
            )}
          </div>
          <div>
            <span className="font-bold">{selectedPlan.name}</span>
            <span
              className={`ml-2 text-xs ${isDark ? "text-violet-400" : "text-violet-500"}`}>
              {selectedPlan.price === 0
                ? "Free"
                : `₦${selectedPlan.price.toLocaleString()}/month`}
            </span>
          </div>
          <button
            type="button"
            onClick={onBack}
            className={`ml-auto text-xs font-semibold flex items-center gap-1 ${isDark ? "text-violet-400 hover:text-violet-300" : "text-violet-600 hover:text-violet-700"}`}>
            <ChevronLeft size={12} />
            Change
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Eligible Market</label>
          <select
            value={selectedMarketId}
            onChange={(event) => onMarketChange(event.target.value)}
            className={inputCls}
            required>
            <option value="">Choose a market covered by this plan</option>
            {markets.map((market) => (
              <option key={market.id} value={market.id}>
                {market.name}{market.city ? `, ${market.city}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Market Location</label>
          <input
            type="text"
            value={marketAddress}
            readOnly
            className={`${inputCls} opacity-70 cursor-not-allowed`}
          />
        </div>
        <div>
          <label className={labelCls}>Shop Name</label>
          <input
            type="text"
            name="shopName"
            value={formData.shopName}
            onChange={onChange}
            placeholder="e.g. GadgetWorld"
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className={labelCls}>Store Address</label>
          <input
            type="text"
            name="storeAddress"
            value={formData.storeAddress}
            onChange={onChange}
            placeholder="e.g. Shop 14, Block A, Balogun Market, Lagos"
            className={inputCls}
            required
          />
        </div>
      </div>

      <div>
        <label className={labelCls}>Note (Optional)</label>
        <textarea
          name="note"
          value={formData.note}
          onChange={onChange}
          placeholder="e.g. Bulk and retail orders accepted daily."
          className={`${inputCls} resize-none`}
          rows={3}
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className={labelCls}>Niche Categories</label>
          <span
            className={`text-[11px] font-semibold ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {selectedCategoryIds.length}/
            {maxCategories >= 999 ? categories.length : maxCategories || 0}
          </span>
        </div>

        <div
          className={`rounded-2xl border p-4 space-y-3 ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}>
          <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Select the niche categories you want approved for this seller
            request.
          </p>

          {loadingCategories ? (
            <div className="py-6 text-center space-y-3">
              <svg
                className="animate-spin w-6 h-6 text-violet-600 mx-auto"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Loading niche categories...
              </p>
            </div>
          ) : !selectedMarketId ? (
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Choose a market covered by this plan to load its categories.
            </p>
          ) : categories.length === 0 ? (
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              No niche categories were returned for this market.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {categories.map((category) => {
                const selected = selectedCategoryIds.includes(category.id);
                const disabled = !selected && categoryLimitReached;

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => !disabled && onToggleCategory(category.id)}
                    className={`rounded-xl border px-4 py-3 text-left transition ${selected
                        ? "border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/10 dark:text-violet-200"
                        : disabled
                          ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-gray-700 dark:bg-gray-900 dark:text-gray-600"
                          : "border-gray-200 bg-white text-gray-700 hover:border-violet-300 hover:bg-violet-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-violet-500/40 dark:hover:bg-violet-500/5"
                      }`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold">{category.name}</span>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">
                        {selected ? "Selected" : disabled ? "Locked" : "Add"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {registrationError && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${isDark
              ? "bg-rose-500/10 border-rose-500/20 text-rose-200"
              : "bg-rose-50 border-rose-200 text-rose-700"
            }`}>
          {registrationError}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300" : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"}`}>
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          type="submit"
          disabled={!isValid || submitting || loadingCategories}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${!isValid || submitting ? "bg-violet-600/50 text-white/60 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-500 text-white"}`}>
          {submitting ? (
            <>
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
              Submitting marketplace request…
            </>
          ) : (
            <>
              <ShoppingBag size={15} />
              {selectedPlan?.isFree || selectedPlan?.price === 0
                ? "Submit Free Plan Request"
                : "Submit Seller Request"}
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
